import { useState, type FormEvent } from 'react';
import { api, fmtDate, kr } from '../api';
import { useData } from '../App';
import { BalanceChart, DeleteButton, Empty, Stat } from '../ui';

/* Income first, then outgoings. The sign is decided by the direction
   toggle, never typed — a minus sign in a number field is how ledgers
   get wrong. */
const CATEGORIES = {
  inn: ['Styrkur', 'Félagsgjöld', 'Þátttökugjöld', 'Sala', 'Gjöf', 'Annað'],
  ut: ['Vörukaup', 'Leiga', 'Búnaður', 'Vinningar', 'Annað'],
} as const;

export default function Utgjold() {
  const { finance, transactions, events, refresh } = useData();

  const [dir, setDir] = useState<'inn' | 'ut'>('inn');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState<string>(CATEGORIES.inn[0]);
  const [busy, setBusy] = useState(false);

  const eventSpend = events.filter((e) => e.spend_isk > 0);

  function switchDir(next: 'inn' | 'ut') {
    setDir(next);
    setCategory(CATEGORIES[next][0]);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const magnitude = Math.abs(Math.round(Number(amount)));
    if (!magnitude) return;
    setBusy(true);
    try {
      await api.addTransaction({
        tx_date: date,
        description: desc.trim(),
        amount_isk: dir === 'inn' ? magnitude : -magnitude,
        category,
      });
      setDesc('');
      setAmount('');
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    if (!window.confirm('Eyða þessari færslu?')) return;
    await api.delTransaction(id);
    await refresh();
  }

  const balance = finance?.balance ?? 0;

  return (
    <div className="view">
      <div className="g-hero">
        <section className="card col-8">
          <div className="card-h">
            <h2>Staða sjóðs</h2>
            <span className="lbl">{finance?.movements ?? 0} færslur</span>
          </div>

          <div className="balance-hero">
            <span className={`amt${balance < 0 ? ' neg' : ''}`}>{kr(balance)}</span>
            <span className="sub">
              {finance && finance.series.length
                ? `Frá ${fmtDate(finance.series[0]?.date ?? null)} til dagsins í dag`
                : 'Engar færslur skráðar enn'}
            </span>
          </div>

          <div style={{ marginTop: 20 }}>
            {finance && finance.series.length > 1 ? (
              <BalanceChart series={finance.series} />
            ) : (
              <Empty
                title="Of fáar færslur fyrir graf"
                body="Þegar peningar hafa hreyfst á tveimur dögum birtist ferillinn hér."
              />
            )}
          </div>
        </section>

        <section className="card col-4">
          <div className="card-h">
            <h2>Ný færsla</h2>
          </div>

          <form className="form" onSubmit={submit}>
            <div className="fld">
              <span className="lbl">Tegund</span>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className={`pk${dir === 'inn' ? ' on' : ''}`}
                  aria-pressed={dir === 'inn'}
                  onClick={() => switchDir('inn')}
                >
                  Inn
                </button>
                <button
                  type="button"
                  className={`pk${dir === 'ut' ? ' on' : ''}`}
                  aria-pressed={dir === 'ut'}
                  onClick={() => switchDir('ut')}
                >
                  Út
                </button>
              </div>
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="t-desc">Lýsing</label>
              <input
                id="t-desc"
                type="text"
                placeholder={dir === 'inn' ? 'Uppbyggingarsjóður Vestfjarða' : 'Leiga á sal'}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                required
              />
            </div>

            <div className="fr">
              <div className="fld">
                <label className="lbl" htmlFor="t-amt">Upphæð (kr.)</label>
                <input
                  id="t-amt"
                  type="number"
                  min={0}
                  step={1}
                  placeholder="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </div>
              <div className="fld">
                <label className="lbl" htmlFor="t-date">Dagsetning</label>
                <input id="t-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
              </div>
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="t-cat">Flokkur</label>
              <select id="t-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES[dir].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>

            <button className="btn" type="submit" disabled={busy || !desc.trim() || !amount}>
              {busy ? 'Vista…' : 'Skrá færslu'}
            </button>
          </form>

          <p style={{ marginTop: 18, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            Kostnaður við einstök kvöld er skráður á viðburðinn sjálfan, ekki hér — annars telst
            hann tvisvar.
          </p>
        </section>
      </div>

      <div className="stats">
        <Stat label="Staða" value={kr(balance)} gold />
        <Stat label="Tekjur alls" value={kr(finance?.income ?? 0)} />
        <Stat label="Gjöld alls" value={kr(Math.abs(finance?.expenses ?? 0))} />
        <Stat label="Viðburðarkostnaður" value={kr(eventSpend.reduce((s, e) => s + e.spend_isk, 0))} />
        <Stat label="Meðaltal á viðburð" value={kr(events.length ? Math.round(events.reduce((s, e) => s + e.spend_isk, 0) / events.length) : 0)} />
        <Stat label="Færslur" value={(finance?.movements ?? 0)} />
      </div>

      <section className="card">
        <div className="card-h">
          <h2>Færslubók</h2>
          <span className="lbl">Nýjast fyrst</span>
        </div>

        {transactions.length === 0 ? (
          <Empty
            title="Engar færslur"
            body="Skráðu styrki, félagsgjöld og annan kostnað hér. Kvöldkostnaður fer á viðburðinn."
          />
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Dagsetning</th>
                  <th>Lýsing</th>
                  <th>Flokkur</th>
                  <th>Upphæð</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id}>
                    <td className="n" data-label="Dagsetning">{fmtDate(t.tx_date)}</td>
                    <td data-label="Lýsing">
                      <div className="ttl2">{t.description}</div>
                    </td>
                    <td data-label="Flokkur">
                      <span className="chip">{t.category}</span>
                    </td>
                    <td className={`n ${t.amount_isk > 0 ? 'amt-in' : 'amt-out'}`} data-label="Upphæð">
                      {t.amount_isk > 0 ? '+' : '−'}
                      {kr(Math.abs(t.amount_isk))}
                    </td>
                    <td className="act">
                      <DeleteButton
                        label={`Eyða ${t.description}`}
                        onClick={() => void remove(t.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Kostnaður á viðburði</h2>
          <span className="lbl">Skráð með hverjum viðburði</span>
        </div>

        {eventSpend.length === 0 ? (
          <Empty title="Ekkert skráð" body="Kostnaður skráist í formið undir Viðburðir." />
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Dagsetning</th>
                  <th>Viðburður</th>
                  <th>Keypt</th>
                  <th>Upphæð</th>
                </tr>
              </thead>
              <tbody>
                {eventSpend.map((e) => (
                  <tr key={e.id}>
                    <td className="n" data-label="Dagsetning">{fmtDate(e.event_date)}</td>
                    <td data-label="Viðburður">
                      <div className="ttl2">{e.name}</div>
                      {e.format && <span className="chip">{e.format}</span>}
                    </td>
                    <td data-label="Keypt">{e.spend_note || '—'}</td>
                    <td className="n" data-label="Upphæð">−{kr(e.spend_isk)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5, maxWidth: '62ch' }}>
        Þetta er grunnurinn að lokaskýrslu til styrktarsjóða. Haltu því réttu jafnóðum —
        Uppbyggingarsjóður og Æskulýðssjóður biðja báðir um sundurliðaðan kostnað.
      </p>
    </div>
  );
}
